"use client"

import { useLiveQuery } from "dexie-react-hooks"
import { offlineDb } from "../db"

/**
 * Reactive row count for a single Dexie table. Pass the table name as
 * Dexie sees it — NOT the kebab-case resource name. Use the
 * `RESOURCE_TABLES` map exported from `@stockline/web` to resolve a
 * resource name (e.g. `"pre-order-flows"`) to its actual table name
 * (e.g. `"preOrderFlows"`); calling `db.table("pre-order-flows")` for
 * a config whose table is `"preOrderFlows"` would throw and the count
 * silently stays at `null`.
 *
 * Returns `null` while Dexie is resolving or if the table doesn't
 * exist; numeric value otherwise. The count ticks live as the engine's
 * full-fetch loop writes pages, so consumers can show honest, growing
 * progress (e.g. "5,000 already loaded") rather than a static
 * "loading…" string during a multi-minute cold start.
 */
export function useResourceRowCount(tableName: string): number | null {
  return (
    useLiveQuery(
      async () => {
        try {
          return await offlineDb.table(tableName).count()
        } catch {
          return null
        }
      },
      [tableName],
      null
    ) ?? null
  )
}
